<script>
/**
 * The MatchTeamGameRegistration view works for the registration work for
 * both teams of the game. 
 */

//--------------------------------------------------
// imported classes
//--------------------------------------------------
import Vue from 'vue'
import { Tabs, Tab, Toast, Switch, Dialog } from 'vant'
import { DateUtil } from '@/utils'
import { getPassport } from '@/libs/passport'
import TeamRegistration from './team-registration.vue'
import GameSummary from './game-summary.vue'
import { getGameDetail, getMatchGameTeamStartingList, updateBMGameLockListOnStage,getGameTeamOfficerList } from '@/api/match-team-game'

//--------------------------------------------------
// global registration
//--------------------------------------------------
Vue.use(Tabs).use(Tab).use(Switch).use(Dialog)

//--------------------------------------------------
// main script
//--------------------------------------------------
export default {
    //--------------------------------------------------
	// component name
	//--------------------------------------------------
	name: 'match-team-game-registration',

    _isReady: false,

    //--------------------------------------------------
    // sub-components
    //--------------------------------------------------
	components: {
        /* The team registration */
		TeamRegistration,
        /* The game summary */
        GameSummary
	},

    //--------------------------------------------------
    // component data
    //--------------------------------------------------
    data() {
        return {
            /* The game info */
            gameInfo: {
                /* The title string */
                title: null,
                /* The time string */
                time: null,
                /* The home team id */
                homeTeamId: null,
                /* The home team name */
                homeTeamName: null,
                /* The home team badge */
                homeTeamBadge: null,
                /* The away team id */
                awayTeamId: null,
                /* The away team name */
                awayTeamName: null,
                /* The away team badge */
                awayTeamBadge: null
            },
            /* The selected team tab which includes home(0) and away(1) */
            selectedTeamTab: 0,
            /**
             * The starter player list for the home team.
             * The list contains the item with {id, name, number, position}
             */
            homeStarterList: [],
            /**
             * The bench player list for the home team.
             * The list contains the item with {id, name, number, position}
             */
            homeBenchList: [],
            /**
             * The starter player list for the away team.
             * The list contains the item with {id, name, number, position}
             */
            awayStarterList: [],
            /**
             * The bench player list for the away team.
             * The list contains the item with {id, name, number, position}
             */
            awayBenchList: [],
            lockListOnStage: true,
            homeOfficerList: [],
            awayOfficerList: []
        }
    },

    //--------------------------------------------------
    // computed setter and getter
    //--------------------------------------------------
    computed: {
        /**
         * @getter
         * Get the home team label.
         */
        homeTeamLabel() {
            if (this.gameInfo.homeTeamName) {
                return '主队：' + this.gameInfo.homeTeamName
            }
            else {
                return ''
            }
        },
        /**
         * @getter
         * Get the away team label.
         */
        awayTeamLabel() {
            if (this.gameInfo.awayTeamName) {
                return '客队：' + this.gameInfo.awayTeamName
            }
            else {
                return ''
            }
        }
    },

    //--------------------------------------------------
    // component methods
    //--------------------------------------------------
    methods: {
        //--------------------------------------------------
        // request method
        //--------------------------------------------------
        /**
         * @request
         * Query the game info and set the bindable data model.
         * @return The reponse object.
         */
        queryGameInfo() {
            const routerParams = this.$route.params
            const params = {
                gameId: routerParams.gameId,
                eventMode: 0,
                simple: 0
            }
            getGameDetail(params).then(response => {
                console.log("get game detail response", response)
                const data = response.data
                // set the game info data
                let title = data.game.matchName
                if (data.game.gameType) {
                    title += ' | ' + data.game.gameType
                }
                if (data.game.roundDescription) {
                    title += ' | ' + data.game.roundDescription
                }
                this.gameInfo.title = title
                const weekDay = DateUtil.parseDateTime(data.game.beginDate, '{a}')
                console.log('weekDay', weekDay)
                const startTime = DateUtil.parseDateTime(data.game.beginDate, '{y}-{m}-{d} {h}:{i}')
                const endTime = DateUtil.parseDateTime(data.game.endDate, '{h}:{i}')
                this.gameInfo.time = '周' + weekDay + ' ' + startTime + '~' + endTime
                this.gameInfo.homeTeamId = data.game.homeTeamId
                this.gameInfo.homeTeamName = data.game.homeTeamName
                this.gameInfo.homeTeamBadge = data.game.homeTeamBadge
                this.gameInfo.awayTeamId = data.game.awayTeamId
                this.gameInfo.awayTeamName = data.game.awayTeamName
                this.gameInfo.awayTeamBadge = data.game.awayTeamBadge
                // query the team roster list by home and away
                this.queryTeamRosterList(true)
                this.queryOfficerList()
            })
        },
        queryOfficerList() {
            const routerParams = this.$route.params
             const params = {
                gameId: routerParams.gameId,
                matchId: routerParams.matchId
             }
            getGameTeamOfficerList({...params,teamId: this.gameInfo.homeTeamId}).then(res => { 
                if (res) {
                    console.log('getGameTeamOfficerList', res)
                    this.homeOfficerList = res.data.list.map(item => {
                        item.regStatus = item.rollCallStatus
                        return item
                    })
                }
            })
            getGameTeamOfficerList({...params,teamId: this.gameInfo.awayTeamId}).then(res => { 
                if (res) {
                    console.log('getGameTeamOfficerList', res)
                    this.awayOfficerList = res.data.list.map(item => {
                        item.regStatus = item.rollCallStatus
                        return item
                    })
                }
            })
        },
        /**
         * @request
         * Query the roster player for each team of the game.
         * After fetch the home team data, it should query the away team one.
         * Try to set the data model for the players of the both teams.
         * @param isHome The home-side boolean status.
         * @return The reponse object.
         */
		queryTeamRosterList(isHome) {
            console.log('queryTeamRosterList', isHome)
            let teamId
            if (isHome) {
                teamId = this.gameInfo.homeTeamId
            }
            else {
                teamId = this.gameInfo.awayTeamId
            }
            const routerParams = this.$route.params
            const params = {
                matchId: routerParams.matchId,
                gameId: routerParams.gameId,
                teamId: teamId
            }
            getMatchGameTeamStartingList(params).then(response => {
                console.log('match game team starting list data', response.data)
                const data = response.data
                // set the starter list
                let starterList = []
                // set the bench list
                let benchList = []
                for (let i = 0; i < data.list.length; i++) {
                    var player = data.list[i]
                    if (player.isStarting == 1) {
                        // starter condition
                        starterList.push({
                            id: player.id,
                            name: player.trueName,
                            number: player.number ? parseInt(player.number) : null,
                            position: player.position,
                            regStatus: player.rollCallStatus
                        })
                    }
                    else if (player.isStarting == 0) {
                        // bench condition
                        benchList.push({
                            id: player.id,
                            name: player.trueName,
                            number: player.number ? parseInt(player.number) : null,
                            position: player.position,
                            regStatus: player.rollCallStatus
                        })
                    }
                }
                this.sortPlayerList(starterList)
                this.sortPlayerList(benchList)
                if (isHome) {
                    console.log('update home roster list')
                    this.homeStarterList = starterList
                    this.homeBenchList = benchList
                    this.queryTeamRosterList()
                }
                else {
                    console.log('update away roster list')
                    this.awayStarterList = starterList
                    this.awayBenchList = benchList
                    this._isReady = true
                    // validate the player list after data loaded
                    this.validatePlayerList()
                }
            })
        },

        //--------------------------------------------------
        // setter and getter
        //--------------------------------------------------

        //--------------------------------------------------
        // handler method
        //--------------------------------------------------
        /**
         * @handler
         * Handle the team switch tab event.
         */
        onSwitchTeamTab(isHome) {
            console.log('onSwitchTeamTab', isHome)
            if (isHome) {
                this.selectedTeamTab = 1
            }
            else {
                this.selectedTeamTab = 0
            }
            this.validatePlayerList()
        },
        onLockListOnStage() {
            console.log('onLockListOnStage', this.lockListOnStage)
            Dialog.confirm({
                title: '提醒',
                message: '非设定状态，不可进行检录动作，是否继续',
                className: 'roster-submit-dialog'
            }).then(() => {
                // on confirm
                updateBMGameLockListOnStage({ gameId: this.$route.params.gameId, lockListOnStage: this.lockListOnStage ? 1 : 0 }).then(res => {
                if (res) {
                    this.$router.go(-1)
                }
            })
            }).catch(() => {
                // on cancel
                this.lockListOnStage = !this.lockListOnStage
            });
            
        },

        //--------------------------------------------------
        // private method
        //--------------------------------------------------
        /**
         * @private
         * Sort the player list with default(0) or name(1) mode via selected 
         * sort type.
         */
        sortPlayerList(playerList) {
            playerList.sort(function(a, b) {
                if (a.number && b.number) {
                    return a.number - b.number
                }
                else if (a.number == null && b.number) {
                    return 1
                }
                else if (a.number && b.number == null) {
                    return -1
                }
                else {
                    return a.id - b.id
                }
            })
        },
        /**
         * @private
         * Validate the player list for the selected team.
         * Show the none player message if starter and bench are empty.
         */
        validatePlayerList() {
            if (this._isReady) {
                if (this.selectedTeamTab === 0) {
                    if (this.homeStarterList.length == 0 && this.homeBenchList.length == 0) {
                        Toast(this.gameInfo.homeTeamName + '暂未设置出场名单')
                    }
                }
                else {
                    if (this.awayStarterList.length == 0 && this.awayBenchList.length == 0) {
                        Toast(this.gameInfo.awayTeamName + '暂未设置出场名单')
                    }
                }
            }
        }
    },
    /**
     * @created
     * Set the data model via the match id, game id and passport.
     * Query the game info data according to the params and cookies.
     * All parameters are required. 
     * Use getPassport to ensure the authentication status.
     */
	created() {
        // validate the passport and prepair the one
        getPassport().then(res => {
            console.log('getPassport', res)
            if (res) {
                this.queryGameInfo()
            }
        })
	}
}
</script>

<template>
    <!-- component -->
    <div class="match-team-game-registration">
        <!-- head start -->
        <div class="head">
            <game-summary
                :title="gameInfo.title"
                :time="gameInfo.time"
                :homeTeamName="gameInfo.homeTeamName"
                :homeTeamBadge="gameInfo.homeTeamBadge"
                :awayTeamName="gameInfo.awayTeamName"
                :awayTeamBadge="gameInfo.awayTeamBadge">
            </game-summary>
        </div>
        <!-- head end -->
         <div class="exit-arrange flex-center">
            <div>出场安排状态</div>
            <div class="flex-center">
                锁定 
                <van-switch v-model="lockListOnStage" class="switch" size="16" active-color="#00B578" @change="onLockListOnStage" />
            </div>
         </div>
        <!-- body start -->
        <div class="body">
            <van-tabs :active="selectedTeamTab" animated>
                <van-tab title="主队">
                    <team-registration 
                        :teamName="homeTeamLabel"
                        :starterList="homeStarterList"
                        :benchList="homeBenchList"
                        :officerList="homeOfficerList"
                        @switch-tab="onSwitchTeamTab(true)">
                    </team-registration>
                </van-tab>
                <van-tab title="客队">
                    <team-registration 
                        :teamName="awayTeamLabel"
                        :starterList="awayStarterList"
                        :benchList="awayBenchList"
                        :officerList="awayOfficerList"
                        @switch-tab="onSwitchTeamTab()">
                    </team-registration>
                </van-tab>
            </van-tabs>
        </div>
        <!-- body end -->
    </div>
</template>

<style>
.match-team-game-registration .body .van-tabs__wrap {
    display: none;
}
.match-team-game-registration .exit-arrange{
    height: 38px;
    background: #F0F0F3;
    padding: 0 22px;
    font-size: 13px;
    color: #676767;
}
.match-team-game-registration .switch{
    margin-left: 12px;
}
</style>